/**
 * Shared pump channel definitions for Diesel and Electric pumps
 * Used across Fleet.tsx, FleetMonitor.tsx, and other components
 */

export interface PumpChannel {
  value: string;
  label: string;
}

export interface PumpChannelGroup {
  label: string;
  options: PumpChannel[];
}

// Universal channels (available on both Diesel and Electric pumps)
export const UNIVERSAL_CHANNELS: PumpChannel[] = [
  { value: 'engine_hours', label: 'Engine Hours' },
  { value: 'pe_hours', label: 'PE Hours' },
  { value: 'fe_hours', label: 'FE Hours' },
  { value: 'engine_load', label: 'Engine Load' },
  { value: 'fan_speed', label: 'Fan Speed' },
  { value: 'discharge_pressure', label: 'Discharge Pressure' },
  { value: 'engine_temp', label: 'Engine Coolant Temperature' },
  { value: 'pe_temp', label: 'Powerend Oil Temperature' },
  { value: 'pe_pressure', label: 'Powerend Oil Pressure' },
  { value: 'throttle', label: 'Throttle' },
  { value: 'voltage', label: 'Voltage' },
  { value: 'calculated_discharge_rate', label: 'Calculated Discharge Rate' },
  { value: 'grease_active', label: 'Grease Active' },
  { value: 'station', label: 'Station' },
  { value: 'stroke_total', label: 'Stroke Total' },
  { value: 'fe_suction_pressure', label: 'FE Suction Pressure' },
];

// Diesel-only channels
export const DIESEL_CHANNELS: PumpChannel[] = [
  { value: 'transmission_hours', label: 'Transmission Hours' },
  { value: 'rpm', label: 'RPM' },
  { value: 'gear', label: 'Gear' },
  { value: 'fuel_rate', label: 'Fuel Rate' },
  { value: 'dual_fuel_ratio', label: 'Dual Fuel Ratio' },
  { value: 'inst_fuel_disp', label: 'Inst Fuel Disp' },
  { value: 'trans_lockup', label: 'Trans Lockup' },
  { value: 'engine_oil_pressure', label: 'Engine Oil Pressure' },
  { value: 'trans_temp', label: 'Trans Temp' },
  { value: 'trans_pressure', label: 'Trans Pressure' },
  { value: 'engine_air_filter_diff', label: 'Engine Air Filter Diff' },
  { value: 'crankcase_pressure', label: 'Crankcase Pressure' },
  { value: 'engine_fuel_pressure', label: 'Engine Fuel Pressure' },
  { value: 'fault_spn_1', label: 'Fault SPN 1' },
  { value: 'fault_fmi_1', label: 'Fault FMI 1' },
  { value: 'fault_spn_2', label: 'Fault SPN 2' },
  { value: 'fault_fmi_2', label: 'Fault FMI 2' },
  { value: 'fault_spn_3', label: 'Fault SPN 3' },
  { value: 'fault_fmi_3', label: 'Fault FMI 3' },
  { value: 'fault_spn_4', label: 'Fault SPN 4' },
  { value: 'fault_fmi_4', label: 'Fault FMI 4' },
  { value: 'fault_spn_5', label: 'Fault SPN 5' },
  { value: 'fault_fmi_5', label: 'Fault FMI 5' },
];

// Electric-only channels
export const ELECTRIC_CHANNELS: PumpChannel[] = [
  // VFD channels
  { value: 'vfd_speed_hz', label: 'VFD Speed Hz' },
  { value: 'vfd_speed_percent', label: 'VFD Speed Percent' },
  { value: 'vfd_power', label: 'VFD Power' },
  { value: 'vfd_energize_hours', label: 'VFD Energize Hours' },
  { value: 'vfd_run_hours', label: 'VFD Run Hours' },
  { value: 'vfd_tripped', label: 'VFD Tripped' },
  { value: 'vfd_warning', label: 'VFD Warning' },
  
  // Atlas channels
  { value: 'atlas_cooling_fan_vfd_trip_code', label: 'Atlas Cooling Fan: VFD Trip Code' },
  { value: 'atlas_outlet_temp', label: 'Atlas: Outlet Temperature' },
  { value: 'atlas_primary_pump_discharge_pressure', label: 'Atlas: Primary Pump Discharge Pressure' },
  { value: 'atlas_reservoir_temp', label: 'Atlas: Reservoir Temperature' },
  { value: 'atlas_secondary_cool_outlet_temp', label: 'Atlas: Secondary Cool Outlet Temperature' },
  { value: 'atlas_secondary_cooler_outlet_temp_1', label: 'Atlas: Secondary Cooler Outlet Temperature 1' },
  { value: 'atlas_secondary_cooler_outlet_temp_2', label: 'Atlas: Secondary Cooler Outlet Temperature 2' },
  { value: 'atlas_secondary_pump_discharge_pressure', label: 'Atlas: Secondary Pump Discharge Pressure' },
  { value: 'atlas_secondary_pump_inlet_temp', label: 'Atlas: Secondary Pump Inlet Temperature' },
  { value: 'atlas_secondary_pump_suction_pressure', label: 'Atlas: Secondary Pump Suction Pressure' },
  
  // Cerberus / Cooling
  { value: 'cerberus_e_stop', label: 'Cerberus: E-Stop' },
  { value: 'cooling_drive_vfd_trip_code', label: 'Cooling Drive: VFD Trip Code' },
  
  // Cube channels
  { value: 'cube_blower_1_state', label: 'Cube: Blower 1 State' },
  { value: 'cube_blower_2_state', label: 'Cube: Blower 2 State' },
  { value: 'cube_coolant_return_pressure', label: 'Cube: Coolant Return Pressure' },
  { value: 'cube_coolant_return_temp', label: 'Cube: Coolant Return Temperature' },
  { value: 'cube_coolant_supply_pressure', label: 'Cube: Coolant Supply Pressure' },
  { value: 'cube_coolant_supply_temp', label: 'Cube: Coolant Supply Temperature' },
  { value: 'cube_e_stop', label: 'Cube: E-Stop' },
  { value: 'cube_moisture_sensor_1', label: 'Cube: Moisture Sensor 1' },
  { value: 'cube_moisture_sensor_2', label: 'Cube: Moisture Sensor 2' },
  { value: 'cube_moisture_sensor_3', label: 'Cube: Moisture Sensor 3' },
  { value: 'cube_transformer_door_closed', label: 'Cube: Transformer Door Closed' },
  { value: 'cube_transformer_phase_a_temp', label: 'Cube: Transformer Phase A Temperature' },
  { value: 'cube_transformer_phase_b_temp', label: 'Cube: Transformer Phase B Temperature' },
  { value: 'cube_transformer_phase_c_temp', label: 'Cube: Transformer Phase C Temperature' },
  
  // Delta / Transformer voltages
  { value: 'delta_insulation_resistance', label: 'Delta Insulation Resistance' },
  { value: 'transformer_delta_voltage_1_2', label: 'Transformer Delta Voltage (1-2)' },
  { value: 'transformer_delta_voltage_2_3', label: 'Transformer Delta Voltage (2-3)' },
  { value: 'transformer_delta_voltage_3_1', label: 'Transformer Delta Voltage (3-1)' },
  
  // Discharge motor channels
  { value: 'discharge_motor_de_bearing_temp', label: 'Discharge Motor: DE Bearing Temperature' },
  { value: 'discharge_motor_nde_bearing_temp', label: 'Discharge Motor: NDE Bearing Temperature' },
  { value: 'discharge_motor_speed', label: 'Discharge Motor: Speed' },
  { value: 'discharge_motor_winding_a1_temp', label: 'Discharge Motor: Winding A1 Temperature' },
  { value: 'discharge_motor_winding_a2_temp', label: 'Discharge Motor: Winding A2 Temperature' },
  { value: 'discharge_motor_winding_b1_temp', label: 'Discharge Motor: Winding B1 Temperature' },
  { value: 'discharge_motor_winding_b2_temp', label: 'Discharge Motor: Winding B2 Temperature' },
  { value: 'discharge_motor_winding_c1_temp', label: 'Discharge Motor: Winding C1 Temperature' },
  { value: 'discharge_motor_winding_c2_temp', label: 'Discharge Motor: Winding C2 Temperature' },
  
  // Discharge VFD channels
  { value: 'discharge_hydraulic_horsepower', label: 'Discharge: Hydraulic Horsepower' },
  { value: 'discharge_vfd_amps', label: 'Discharge: VFD Amps' },
  { value: 'discharge_vfd_cdc_temp', label: 'Discharge: VFD CDC Temperature' },
  { value: 'discharge_vfd_dc_link_volts', label: 'Discharge: VFD DC Link Volts' },
  { value: 'discharge_vfd_igbt_1_temp', label: 'Discharge: VFD IGBT 1 Temperature' },
  { value: 'discharge_vfd_igbt_2_temp', label: 'Discharge: VFD IGBT 2 Temperature' },
  { value: 'discharge_vfd_igbt_3_temp', label: 'Discharge: VFD IGBT 3 Temperature' },
  { value: 'discharge_vfd_rectifier_temp', label: 'Discharge: VFD Rectifier Temperature' },
  
  // Motor speed
  { value: 'motor_actual_speed', label: 'Motor Actual Speed' },
  { value: 'motor_target_speed', label: 'Motor Target Speed' },
  { value: 'discharge_vfd_trip_code', label: 'Discharge: VFD Trip Code' },
  { value: 'discharge_vfd_warning_code', label: 'Discharge: VFD Warning Code' },
  
  // Low level alarms
  { value: 'grease_level_low', label: 'Grease Level Low' },
  { value: 'pe_oil_level_low', label: 'PE Oil Level Low' },
  
  // MCC channels
  { value: 'mcc_insulation_resistance', label: 'MCC Insulation Resistance' },
  { value: 'mcc_voltage_1_2', label: 'MCC Voltage (1-2)' },
  { value: 'mcc_voltage_2_3', label: 'MCC Voltage (2-3)' },
  { value: 'mcc_voltage_3_1', label: 'MCC Voltage (3-1)' },
  
  // WYE channels
  { value: 'wye_insulation_resistance', label: 'WYE Insulation Resistance' },
  { value: 'transformer_wye_voltage_1_2', label: 'Transformer WYE Voltage (1-2)' },
  { value: 'transformer_wye_voltage_2_3', label: 'Transformer WYE Voltage (2-3)' },
  { value: 'transformer_wye_voltage_3_1', label: 'Transformer WYE Voltage (3-1)' },
];

// Grouped channels for react-select with group headers
export const GROUPED_PUMP_CHANNELS: PumpChannelGroup[] = [
  { label: 'Universal', options: UNIVERSAL_CHANNELS },
  { label: 'Diesel', options: DIESEL_CHANNELS },
  { label: 'Electric', options: ELECTRIC_CHANNELS },
];

// Flat list of all channels (for backwards compatibility)
export const ALL_PUMP_CHANNELS: PumpChannel[] = [
  ...UNIVERSAL_CHANNELS,
  ...DIESEL_CHANNELS,
  ...ELECTRIC_CHANNELS,
];

// Helper function to find a channel by value
export const findChannelByValue = (value: string): PumpChannel | undefined => {
  return ALL_PUMP_CHANNELS.find(ch => ch.value === value);
};

// Helper function to get channel label by value
export const getChannelLabel = (value: string): string => {
  const channel = findChannelByValue(value);
  return channel?.label || value;
};

// Channels that only ever report whole numbers (gear, on/off flags, codes).
const WHOLE_NUMBER_CHANNEL_VALUES = new Set<string>([
  'gear',
  'trans_lockup',
  'grease_active',
  'grease_level_low',
  'pe_oil_level_low',
  'vfd_tripped',
  'vfd_warning',
  'cerberus_e_stop',
  'cube_e_stop',
  'cube_blower_1_state',
  'cube_blower_2_state',
  'cube_transformer_door_closed',
  'atlas_cooling_fan_vfd_trip_code',
  'cooling_drive_vfd_trip_code',
  'discharge_vfd_trip_code',
  'discharge_vfd_warning_code',
  'fault_spn_1',
  'fault_fmi_1',
  'fault_spn_2',
  'fault_fmi_2',
  'fault_spn_3',
  'fault_fmi_3',
  'fault_spn_4',
  'fault_fmi_4',
  'fault_spn_5',
  'fault_fmi_5',
]);

export const isWholeNumberChannel = (channelValue: string): boolean =>
  WHOLE_NUMBER_CHANNEL_VALUES.has(channelValue);

// The 1 Min / 1 Hour rollups store the AVERAGE of each bucket, so a gear
// change inside a minute comes back as e.g. 3.5. For whole-number channels,
// show the higher of the two neighbouring whole numbers instead (3.5 -> 4).
// Float noise (3.0000001) is treated as the whole number it is.
export const toWholeNumberReading = (
  channelValue: string,
  value: number,
): number => {
  if (!Number.isFinite(value) || !isWholeNumberChannel(channelValue)) {
    return value;
  }
  const nearest = Math.round(value);
  return Math.abs(value - nearest) < 1e-6 ? nearest : Math.ceil(value);
};
